import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  approveAdditionalWfhQuotaRequest,
  getAdditionalWfhQuotaRequestsForApprover,
  getAdditionalWfhReportees,
  rejectAdditionalWfhQuotaRequest,
} from '../../services/AdditionalWfhQuotaService';
import { ERROR_KEY, SHOW_NOTIFICATION, WFHAttribute } from '../../constants';
import PaginationComponent from '../../components/Pagination/Pagination';
import GenericLoader from '../../components/Loader/GenericLoader';
import AdditionalWfhQuotaRequestCard from '../../components/AdditionalWfhQuota/AdditionalWfhQuotaRequestCard';
import AdditionalWfhQuotaFilters from '../../components/AdditionalWfhQuota/AdditionalWfhQuotaFilters';

const defaultFilters = () => ({
  status: 'PENDING',
  empId: '',
  requestDateFrom: '',
  requestDateTo: '',
  quotaYear: '',
  quarterIndex: '',
});

/**
 * Senior Manager inbox for Additional WFH quota requests
 * (Reporting Manager with senior-manager role).
 */
const SeniorManagerWfhQuotaComponent = () => {
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [reportees, setReportees] = useState([]);
  const [loader, setLoader] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [refresh, setRefresh] = useState(false);

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const loadReportees = useCallback(async () => {
    const response = await getAdditionalWfhReportees('', 0, 200);
    if (!response.isError) {
      setReportees(response.data?.data?.content || response.data?.content || []);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setLoader(true);
    const response = await getAdditionalWfhQuotaRequestsForApprover(
      filters,
      pageNo,
      pageSize,
    );
    setLoader(false);
    if (!response.isError) {
      const pageData = response.data?.data || {};
      setRequests(pageData.content || []);
      setTotalPages(pageData.totalPages || 0);
    } else {
      notify(ERROR_KEY, response.message);
    }
  }, [filters, pageNo, pageSize]);

  useEffect(() => {
    loadReportees();
  }, [loadReportees]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, refresh]);

  useEffect(() => {
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  }, [filters]);

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefresh((prev) => !prev);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  };

  const handleApprove = async (request) => {
    const response = await approveAdditionalWfhQuotaRequest(request.id);
    if (!response.isError) {
      notify('success', 'Request approved — WFH quota increased');
      setRefresh((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  const handleReject = async (request) => {
    const response = await rejectAdditionalWfhQuotaRequest(request.id);
    if (!response.isError) {
      notify('success', 'Request rejected');
      setRefresh((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  return (
    <Box className="main-content-overlay">
      <div className="main-content-div">
        <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
          WFH Quota Requests
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Employees request additional WFH days for a specific quarter. Approving
          adds the requested days to their current quarterly quota.
        </Typography>

        <AdditionalWfhQuotaFilters
          filters={filters}
          setFilters={setFilters}
          reportees={reportees}
        />

        {loader ? (
          <GenericLoader
            rectAttributes={WFHAttribute}
            width="100%"
            height={400}
          />
        ) : requests.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '40vh',
            }}
          >
            <Typography color="text.secondary">
              No Additional WFH quota requests found.
            </Typography>
          </Box>
        ) : (
          requests.map((req) => (
            <AdditionalWfhQuotaRequestCard
              key={req.id}
              request={req}
              variant="manager"
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))
        )}

        {requests.length !== 0 && (
          <Box className="page-holder" sx={{ marginBottom: '10px' }}>
            <PaginationComponent
              count={totalPages}
              page={page}
              pageChange={pageChange}
              pageSize={pageSize}
              handlePageSizeChange={handlePageSizeChange}
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

export default SeniorManagerWfhQuotaComponent;
